import { Children, CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { SliderArrowButtons } from './SliderArrowButtons'

const useStyles = makeStyles({
  root: { position: 'relative', minWidth: 0, maxWidth: '100%' },
  track: {
    display: 'flex', width: '100%', height: '100%', boxSizing: 'content-box',
    overflowX: 'auto', overflowY: 'hidden', overscrollBehaviorX: 'contain',
    scrollSnapType: 'x mandatory', scrollbarWidth: 'none', touchAction: 'pan-x pan-y pinch-zoom',
    gap: 'var(--native-slider-gap)',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > .swiper-slide': { scrollSnapAlign: 'start', flexShrink: 0, height: 'auto' },
    '@media (min-width:600px)': { gap: 'var(--native-slider-gap-sm)' },
    '@media (min-width:960px)': { gap: 'var(--native-slider-gap-md)' },
    '@media (min-width:1280px)': { gap: 'var(--native-slider-gap-lg)' },
    '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: -2 },
  },
})

type Props = {
  children: ReactNode
  label: string
  spaceBetween: number
  breakpoints?: Record<number, { spaceBetween: number }>
  showArrows?: boolean
}

/** Preserve the existing auto-sized visible-group navigation at real card starts. */
export const nativeScrollTarget = (slides: { start: number; width: number }[], current: number, viewport: number, maximum: number, direction: number) => {
  if (!slides.length) return 0
  let active = 0
  for (let index = 1; index < slides.length; index++) {
    if (current >= (slides[index - 1].start + slides[index].start) / 2) active = index
  }
  let targetIndex: number
  if (direction > 0) {
    const visible = 1 + slides.slice(active + 1).filter(slide => slide.start + slide.width - slides[active].start < viewport).length
    targetIndex = Math.min(slides.length - 1, active + visible)
  } else {
    const previous = slides.reduce((index, slide, next) => slide.start < Math.floor(current) && slide.start <= maximum ? next : index, 0)
    const visible = 1 + slides.slice(0, active).filter(slide => slides[active].start - slide.start < viewport).length
    targetIndex = Math.max(0, previous - visible + 1)
  }
  return Math.max(0, Math.min(maximum, slides[targetIndex].start))
}

/** Native scrolling keeps every SSR card while avoiding per-slide Swiper measurements. */
const NativeHorizontalSlider = ({ children, label, spaceBetween, breakpoints, showArrows = true }: Props) => {
  const classes = useStyles()
  const track = useRef<HTMLDivElement>(null)
  const slideCount = Children.count(children)
  const [state, setState] = useState({ available: false, beginning: true, end: false })
  useEffect(() => {
    const element = track.current
    if (!element) return
    let frame = 0
    const update = () => {
      frame = 0
      const maximum = Math.max(0, element.scrollWidth - element.clientWidth)
      const next = { available: maximum > 1, beginning: element.scrollLeft <= 1, end: element.scrollLeft >= maximum - 1 }
      setState(current => current.available === next.available && current.beginning === next.beginning && current.end === next.end ? current : next)
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update) }
    element.addEventListener('scroll', schedule, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule)
    observer?.observe(element)
    if (element.firstElementChild) observer?.observe(element.firstElementChild)
    if (element.lastElementChild !== element.firstElementChild && element.lastElementChild) observer?.observe(element.lastElementChild)
    window.addEventListener('resize', schedule)
    update()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      element.removeEventListener('scroll', schedule)
      observer?.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [slideCount])

  const move = (direction: number) => {
    const element = track.current
    if (!element) return
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const children = Array.from(element.children) as HTMLElement[]
    const origin = children[0]?.offsetLeft || 0
    const slides = children.map(child => ({ start: child.offsetLeft - origin, width: child.offsetWidth }))
    const left = nativeScrollTarget(slides, element.scrollLeft, element.clientWidth, element.scrollWidth - element.clientWidth, direction)
    element.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' })
  }
  const sm = breakpoints?.[600]?.spaceBetween ?? spaceBetween
  const md = breakpoints?.[960]?.spaceBetween ?? sm
  const lg = breakpoints?.[1280]?.spaceBetween ?? md
  return <div className={`swiper swiper-horizontal swiper-css-mode ${classes.root}`} style={{
    '--native-slider-gap': `${spaceBetween}px`, '--native-slider-gap-sm': `${sm}px`,
    '--native-slider-gap-md': `${md}px`, '--native-slider-gap-lg': `${lg}px`,
  } as CSSProperties}>
    <div ref={track} className={`swiper-wrapper ${classes.track}`} role='group' aria-label={label} tabIndex={0}
      onKeyDown={event => {
        if (event.target !== event.currentTarget || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
        event.preventDefault()
        move(event.key === 'ArrowRight' ? 1 : -1)
      }}>
      {children}
    </div>
    {showArrows && <SliderArrowButtons {...state} label={label} onPrevious={() => move(-1)} onNext={() => move(1)} />}
  </div>
}

export const NativeSliderSlide = ({ children, className = '' }: { children: ReactNode; className?: string }) =>
  <div className={className ? `swiper-slide ${className}` : 'swiper-slide'}>{children}</div>

export default NativeHorizontalSlider
